import { loop, Cmd } from 'redux-loop';
import { history } from 'store';

import { parseResponse } from 'utils';
import { tryRefreshFollowUrl } from 'utils/sso';
import { changeLocale } from '../intl'; // NOTICE: 'intl' is a node package
import config from 'config';

const DEFAULT_LOCALE = navigator?.language || navigator?.userLanguage || 'en-US';

const requestPush = async (url) => {
  history.push(url);
  return Promise.resolve();
};

// LOGOUT ACTIONS
const requestLogout = async () => {
  await fetch(`${config.AUTH_URL}/identity`, {
    method: 'DELETE',
    mode: 'cors',
    credentials: 'include',
  });
  return true;
};

const logoutSuccess = () => ({
  type: 'LOGOUT_COMPLETE',
});

const logoutFail = () => ({
  type: 'LOGOUT_FAILED',
});

// IDENTITY ACTIONS
const requestIdentity = async () => {
  const response = await fetch(`${config.AUTH_URL}/identity`, {
    method: 'PUT',
    mode: 'cors',
    credentials: 'include',
  }).then(tryRefreshFollowUrl);

  const result = await parseResponse(response);
  return result;
};

const identitySuccess = ({ access_token, user }) => ({
  type: 'IDENTITY_COMPLETE',
  access_token,
  user,
});

const identityFail = ({ code, text }) => ({
  type: 'IDENTITY_FAILED',
  code,
  text,
});

// REFRESH ACTIONS
const requestRefreshToken = async () => {
  const response = await fetch(`${config.AUTH_URL}/refresh`, {
    method: 'PUT',
    mode: 'cors',
    credentials: 'include',
  }).then(tryRefreshFollowUrl);

  const result = await parseResponse(response);
  return result;
};

const refreshTokenSuccess = ({ access_token }) => ({
  type: 'REFRESH_TOKEN_COMPLETE',
  access_token,
});

const refreshTokenFail = ({ code, text }) => ({
  type: 'REFRESH_TOKEN_FAILED',
  code,
  text,
});

const initialState = {
  refreshing_token: false,
  logged_in: false,
  defaults: {},
  error: null,
};

const reducer = (state = initialState, action) => {
  switch(action.type) {
    case 'IDENTITY_REQUEST':
      if(state.refreshing_token) {
        // Already refreshing, move along...
        return state;
      }
      return loop({
        ...state,
        refreshing_token: true,
      }, Cmd.run(requestIdentity, {
        successActionCreator: identitySuccess,
        failActionCreator: identityFail,
        args: [],
      }));
    case 'REFRESH_TOKEN_REQUEST':
      if(state.refreshing_token) {
        // Already refreshing, move along...
        return state;
      }
      return loop({
        ...state,
        refreshing_token: true,
      }, Cmd.run(requestRefreshToken, {
        successActionCreator: refreshTokenSuccess,
        failActionCreator: refreshTokenFail,
        args: [],
      }));
    case 'LOGIN_COMPLETE':
      return loop({
        ...initialState,
        logged_in: true,
      }, Cmd.action(changeLocale((action.user || {}).language || DEFAULT_LOCALE)));
    case 'SUBMIT_RECOVERED_PASSWORD_COMPLETE':
      return loop({
        ...initialState,
        logged_in: true,
      }, Cmd.list([
        Cmd.run(requestPush, { args: ['/'] }),
        Cmd.action(changeLocale((action.user || {}).language || DEFAULT_LOCALE)),
      ]));
    case 'REFRESH_TOKEN_COMPLETE':
    case 'IDENTITY_COMPLETE':
      return {
        ...initialState,
        refreshing_token: false,
        logged_in: true,
      };
    case 'LOGOUT_REQUEST':
      return loop(state, Cmd.run(requestLogout, {
        successActionCreator: logoutSuccess,
        failActionCreator: logoutFail,
        args: [],
      }));
    case 'LOGOUT_COMPLETE':
    case 'LOGOUT_FAILED':
      return loop(initialState, Cmd.list([
        Cmd.run(requestPush, { args: ['/signin'] }),
        Cmd.action(changeLocale(DEFAULT_LOCALE)),
      ]));
    case 'LOOKUP_FAILED':
    case 'LOGIN_FAILED':
      return initialState;
    case 'REFRESH_TOKEN_FAILED':
    case 'IDENTITY_FAILED':
      return initialState; // TODO: process error
    case 'RESET_AUTH':
      return initialState;
    case 'HYDRATE_STORE':
      return state; // TODO: consider storing email, using action.state
    default:
      return state;
  }
};

export {
  reducer as auth,
};
